import { fetchWithAuth } from '@src/js/utils/http';
import config from '@src/js/config';
import { EventEmitter } from '@src/js/utils/EventEmitter';
import { AssistantTranscriptService } from "@src/js/services/assistantTranscriptService";
export var OpenAIManagerEvents;
(function (OpenAIManagerEvents) {
    OpenAIManagerEvents["DONE"] = "done";
    OpenAIManagerEvents["CHUNK"] = "chunk";
    OpenAIManagerEvents["SENTENCE"] = "sentence";
    OpenAIManagerEvents["ERROR"] = "error";
    OpenAIManagerEvents["NOTIFICATION"] = "notification";
    OpenAIManagerEvents["INTERRUPTED"] = "interrupted";
})(OpenAIManagerEvents || (OpenAIManagerEvents = {}));
/**
 * OpenAIManager
 *
 * Manages communication with the OpenAI backend for chat messages and avatar control.
 * - Sends messages to OpenAI and processes streaming responses
 * - Manages notifications and conversation history
 * - Allows manual sentence addition
 * - Supports interruption and destruction of the instance
 */
export class OpenAIManager extends EventEmitter {
    constructor(assistant) {
        super();
        this.controller = null;
        this.currentNotification = null;
        this.currentNotificationId = null;
        this.currentUserMsgIndex = null; // ⭐ new
        this.isStreaming = false;
        this.notifications = [];
        this.buffer = '';
        this.conversationHistory = [];
        this.assistant = assistant;
        this.sessionId = crypto.randomUUID();
        // create initial transcript object
        let transcript = {
            assistantShareKey: this.assistant.shareKey || '',
            chatMessages: [],
            sessionId: this.sessionId,
        };
        AssistantTranscriptService.save(transcript)
            .then(() => {
            console.log('Transcript initialized successfully');
        })
            .catch(err => {
            console.error('Failed to initialize transcript:', err);
        });
    }
    static async init(assistant) {
        return new OpenAIManager(assistant);
    }
    /* ---------------- main request ---------------- */
    async sendMessageAndStartRun(text) {
        // hard-cancel any current stream
        this.interrupt();
        this.isStreaming = true;
        this.currentNotificationId = crypto.randomUUID();
        /* ---- UI notification ---- */
        const notification = {
            type: 'is-info',
            message: `Starting run with message: ${text}`,
            icon: 'fas fa-spinner fa-spin',
            time: new Date().toLocaleTimeString(),
            notificationId: this.currentNotificationId,
        };
        this.addNotification(notification);
        this.currentNotification = notification;
        /* ---- push user message & remember its index ---- */
        let message = { role: 'user', content: text };
        this.currentUserMsgIndex =
            this.conversationHistory.push(message) - 1;
        if (!this.assistant || !this.assistant.shareKey) {
            throw new Error('Missing assistant or shareKey');
        }
        AssistantTranscriptService.addMessage(this.assistant.shareKey, this.sessionId, message);
        /* ---- fetch stream ---- */
        const url = `${config.API_URL}chat/openai/threads/${this.assistant.shareKey}/completions/stream`;
        const payload = { messages: this.conversationHistory };
        this.controller = new AbortController();
        try {
            const response = await fetchWithAuth(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
                body: JSON.stringify(payload),
                signal: this.controller.signal,
            });
            if (!response.body)
                throw new Error('No response body from stream');
            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let partial = '';
            let assistantReply = '';
            let done = false;
            while (!done) {
                const { value, done: readerDone } = await reader.read();
                done = readerDone;
                if (!value)
                    continue;
                for (const raw of decoder.decode(value, { stream: true }).split('\n')) {
                    if (!raw.startsWith('data:'))
                        continue;
                    const clean = raw.replace(/^data:\s*/, '').trim();
                    if (!clean)
                        continue;
                    /* ---- end of stream ---- */
                    if (clean === '[DONE]') {
                        this.handleStreamEnd(assistantReply.trim());
                        return;
                    }
                    if (!clean.startsWith('{'))
                        continue;
                    partial += clean;
                    try {
                        const parsed = JSON.parse(partial);
                        partial = '';
                        const chunk = parsed?.delta?.content?.[0]?.text?.value ??
                            parsed?.choices?.[0]?.delta?.content ??
                            '';
                        if (chunk) {
                            this.buffer += chunk;
                            assistantReply += chunk;
                            this.emit('chunk', chunk);
                            if (/[.!?]$/.test(this.buffer.trim())) {
                                const sentence = this.buffer;
                                this.buffer = '';
                                const sentenceNote = {
                                    type: 'is-warning',
                                    message: sentence,
                                    icon: 'fa-regular fa-circle-check',
                                    time: new Date().toLocaleTimeString(),
                                    notificationId: this.currentNotificationId ?? undefined,
                                };
                                this.addNotification(sentenceNote);
                                this.emit('sentence', sentence);
                                this.emit('notification', sentenceNote);
                            }
                        }
                    }
                    catch {
                        /* wait for next fragment */
                    }
                }
            }
        }
        catch (err) {
            this.isStreaming = false;
            if (err.name === 'AbortError')
                return;
            console.error('Streaming request failed:', err);
            this.emit('error', err);
        }
    }
    /* ---------------- external interrupt ---------------- */
    interrupt() {
        if (this.controller && this.isStreaming) {
            console.log("[OpenAI] Interrupting stream...");
            // Immediately set streaming to false to prevent race conditions
            this.isStreaming = false;
            // Abort the fetch request
            this.controller.abort();
            // Clear any buffered content
            this.buffer = '';
            /* 🔥 remove the pending user message */
            if (this.currentUserMsgIndex !== null) {
                this.conversationHistory.splice(this.currentUserMsgIndex, 1);
                this.currentUserMsgIndex = null;
            }
            /* update UI */
            if (this.currentNotification) {
                this.currentNotification.icon = 'fas fa-ban';
                this.currentNotification.message = 'Interrupted';
                this.emit('notification', this.currentNotification);
            }
            console.log("[OpenAI] Stream interrupted successfully");
            this.emit('interrupted', undefined);
        }
        else if (this.isStreaming) {
            // Edge case: streaming but no controller
            console.warn("[OpenAI] Streaming detected but no controller found, forcing stop");
            this.isStreaming = false;
            this.buffer = '';
            this.emit('interrupted', undefined);
        }
    }
    /* ---------------- helpers ---------------- */
    handleStreamEnd(finalText) {
        this.isStreaming = false;
        this.currentUserMsgIndex = null; // keep user message now
        if (finalText) {
            let message = { role: 'assistant', content: finalText };
            this.conversationHistory.push(message);
            if (!this.assistant || !this.assistant.shareKey) {
                throw new Error('Missing assistant or shareKey');
            }
            AssistantTranscriptService.addMessage(this.assistant.shareKey, this.sessionId, message);
        }
        if (this.currentNotification) {
            this.currentNotification.icon = 'fa-regular fa-circle-check';
            this.emit('notification', this.currentNotification);
        }
        this.emit('done', undefined);
    }
    addNotification(n) {
        this.notifications.unshift(n);
        this.emit('notification', n);
    }
    addManualSentence(sentence) {
        // Notification wie bei OpenAI
        const sentenceNote = {
            type: 'is-warning',
            message: sentence,
            icon: 'fa-regular fa-circle-check',
            time: new Date().toLocaleTimeString(),
            notificationId: this.currentNotificationId ?? undefined,
        };
        this.addNotification(sentenceNote);
        this.emit('sentence', sentence);
        this.emit('notification', sentenceNote);
    }
    getConversationHistory() {
        return this.conversationHistory;
    }
    getSessionId() {
        return this.sessionId;
    }
    destroy() {
        // Stop any ongoing stream
        this.interrupt();
        // Clear internal state
        this.controller = null;
        this.currentNotification = null;
        this.currentNotificationId = null;
        this.currentUserMsgIndex = null;
        this.isStreaming = false;
        // Clear stored data
        this.notifications = [];
        this.buffer = '';
        this.conversationHistory = [];
        // Clear event listeners from EventEmitter
        this.removeAllListeners();
        // Nullify assistant reference if needed
        this.assistant = {};
        console.log('[OpenAIManager] Destroyed successfully');
    }
}
